import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Snackbar,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { ActivityTypes, logActivity } from '../utils/activityLogger';

const Adverts = () => {
  const theme = useTheme();
  const [adverts, setAdverts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentAdvert, setCurrentAdvert] = useState({
    title: '',
    description: '',
    image: null,
    link: '',
    startDate: '',
    endDate: '',
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    fetchAdverts();
  }, []);

  const fetchAdverts = async () => {
    try {
      setLoading(true);
      const response = await api.adverts.getAllAdverts();
      if (response.success) {
        setAdverts(response.data || []);
      } else {
        throw new Error(response.message || 'Failed to fetch adverts');
      }
    } catch (error) {
      console.error('Fetch adverts error:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to fetch adverts. Please try again.',
        severity: 'error'
      });
      setAdverts([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentAdvert({
      title: '',
      description: '',
      image: null,
      link: '',
      startDate: '',
      endDate: '',
    });
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setCurrentAdvert(prev => ({
      ...prev,
      [name]: files ? files[0] : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!currentAdvert.title || !currentAdvert.description || !currentAdvert.startDate || !currentAdvert.endDate) {
      setSnackbar({
        open: true,
        message: 'Please fill in all required fields',
        severity: 'error'
      });
      return;
    }

    // Validate dates
    const startDate = new Date(currentAdvert.startDate);
    const endDate = new Date(currentAdvert.endDate);
    const now = new Date();

    // if (startDate < now) {
    //   setSnackbar({
    //     open: true,
    //     message: 'Start date cannot be in the past',
    //     severity: 'error'
    //   });
    //   return;
    // }

    if (endDate <= startDate) {
      setSnackbar({
        open: true,
        message: 'End date must be after start date',
        severity: 'error'
      });
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Format dates to ISO string
      const formattedData = {
        ...currentAdvert,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      };

      console.log('Submitting data:', formattedData);
      const response = await api.adverts.createAdvert(formattedData);
      
      if (response.success) {
        setAdverts(prev => [...prev, response.data.advert]);
        setSnackbar({
          open: true,
          message: response.message || 'Advert added successfully!',
          severity: 'success'
        });
        handleCloseDialog();
        fetchAdverts(); // Refresh the list
      } else {
        throw new Error(response.message || 'Failed to add advert');
      }
    } catch (error) {
      console.error('Submit error:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to add advert. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.adverts.deleteAdvert(id);
      setAdverts(prev => prev.filter(advert => advert._id !== id));
      setSnackbar({
        open: true,
        message: 'Advert deleted successfully!',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to delete advert. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <LoadingSpinner size="medium" />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={700} color="#A80A0A">
          Adverts
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            '&:hover': {
              boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
            },
          }}
        >
          Add New Advert
        </Button>
      </Box>

      <Grid container spacing={3}>
        {adverts.map((advert) => (
          <Grid item xs={12} sm={6} md={4} key={advert._id}>
            <Card 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 2,
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                '&:hover': {
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                },
              }}
            >
              <CardMedia
                component="img"
                height="140"
                image={advert.image}
                alt={advert.title}
                sx={{ objectFit: 'cover' }}
              />
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography gutterBottom variant="h6" component="div" fontWeight={600}>
                  {advert.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {advert.description}
                </Typography>
                {advert.link && (
                  <Typography variant="body2" color="primary" sx={{ mb: 1 }}>
                    <a href={advert.link} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                      {advert.link}
                    </a>
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {`${new Date(advert.startDate).toLocaleDateString()} - ${new Date(advert.endDate).toLocaleDateString()}`}
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
                <IconButton 
                  size="small" 
                  color="primary"
                  sx={{
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.1)',
                    },
                  }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton 
                  size="small" 
                  color="error" 
                  onClick={() => handleDelete(advert._id)}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'rgba(211, 47, 47, 0.1)',
                    },
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>Add New Advert</DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Title"
              name="title"
              value={currentAdvert.title}
              onChange={handleChange}
              required
              margin="normal"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={currentAdvert.description}
              onChange={handleChange}
              required
              multiline
              rows={3}
              margin="normal"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Link"
              name="link"
              value={currentAdvert.link}
              onChange={handleChange}
              margin="normal"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Start Date"
              name="startDate"
              type="date"
              value={currentAdvert.startDate}
              onChange={handleChange}
              required
              margin="normal"
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="End Date"
              name="endDate"
              type="date"
              value={currentAdvert.endDate}
              onChange={handleChange}
              required
              margin="normal"
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
            />
            <Button
              variant="outlined"
              component="label"
              fullWidth
              sx={{ 
                mt: 2,
                mb: 2,
                borderRadius: 2,
                textTransform: 'none',
              }}
            >
              {currentAdvert.image ? 'Change Image' : 'Upload Image'}
              <input
                type="file"
                name="image"
                hidden
                accept="image/*"
                onChange={handleChange}
              />
            </Button>
            {currentAdvert.image && (
              <Box sx={{ mt: 2, mb: 2, textAlign: 'center' }}>
                <img
                  src={URL.createObjectURL(currentAdvert.image)}
                  alt="Preview"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '200px',
                    borderRadius: '8px',
                  }}
                />
              </Box>
            )}
          </form>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleCloseDialog}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={isSubmitting}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              minWidth: 100,
            }}
          >
            {isSubmitting ? 'Adding...' : 'Add Advert'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ 
            width: '100%',
            borderRadius: 2,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Adverts; 